import { Button, Splitter } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';

export interface ITogglePanelItem {
  key: string;
  label: React.ReactNode;
  children: React.ReactNode;
  defaultSize?: string;
}

export interface ITogglePanelsProps {
  items: ITogglePanelItem[];
  defaultActiveKeys?: string[];
  activeKeys?: string[];
  onChange?: (keys: string[]) => void;
  direction?: 'horizontal' | 'vertical';
  height?: number | string;
  minPanelSize?: string;
  buttonBarStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  activeButtonStyle?: React.CSSProperties;
  panelContainerStyle?: React.CSSProperties;
  componentStyle?: React.CSSProperties;
}

const TogglePanels: React.FC<ITogglePanelsProps> = ({
  items,
  defaultActiveKeys,
  activeKeys: controlledKeys,
  onChange,
  direction = 'horizontal',
  height = '100%',
  minPanelSize = '20%',
  buttonBarStyle,
  buttonStyle,
  activeButtonStyle,
  panelContainerStyle,
  componentStyle,
}) => {
  const [internalKeys, setInternalKeys] = useState<string[]>(
    () => defaultActiveKeys ?? items.map(i => i.key)
  );

  const activeKeys = controlledKeys ?? internalKeys;

  const toggle = (key: string) => {
    const next = activeKeys.includes(key)
      ? activeKeys.filter(k => k !== key)
      : [...activeKeys, key];

    if (!controlledKeys) setInternalKeys(next);
    onChange?.(next);
  };

  const activeItems = items.filter(i => activeKeys.includes(i.key));
  const resolvedHeight = activeItems.length === 1 ? 'auto' : height;

  const calculatePadding = (index: number) => {
    if (activeItems.length <= 1) {
      return 0;
    }

    if (index === 0) {
      return '0 8px 0 0';
    }

    if (index === activeItems.length - 1) {
      return '0 0 0 8px';
    }

    return '0 8px';
  };

  return (
    <Wrapper style={{ height: resolvedHeight, ...componentStyle }}>
      <ButtonBar style={buttonBarStyle}>
        {items.map(item => {
          const isActive = activeKeys.includes(item.key);

          return (
            <Button
              key={item.key}
              type={isActive ? 'primary' : 'default'}
              size="small"
              onClick={() => toggle(item.key)}
              style={isActive ? activeButtonStyle : buttonStyle}
            >
              {item.label}
            </Button>
          );
        })}
      </ButtonBar>

      {activeItems.length > 0 && (
        <SplitterContainer style={panelContainerStyle}>
          <Splitter layout={direction} style={{ height: '100%' }}>
            {activeItems.map((item, index) => (
              <Splitter.Panel
                key={item.key}
                min={minPanelSize}
                size={item.defaultSize}
                style={{
                  minHeight: 0,
                  overflow: 'hidden',
                  padding: calculatePadding(index),
                }}
              >
                <PanelScroll $scroll={activeItems.length > 1}>
                  {item.children}
                </PanelScroll>
              </Splitter.Panel>
            ))}
          </Splitter>
        </SplitterContainer>
      )}
    </Wrapper>
  );
};

export default TogglePanels;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const ButtonBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const SplitterContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const PanelScroll = styled.div<{ $scroll: boolean }>`
  height: ${({ $scroll }) => ($scroll ? '100%' : 'auto')};
  min-height: 0;
  overflow-y: ${({ $scroll }) => ($scroll ? 'auto' : 'visible')};
  overflow-x: hidden;
  box-sizing: border-box;
`;
